/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/require-default-props */
import React, { PropsWithChildren } from 'react';
import { Dimensions, StyleSheet, Text as DefaultText, TextStyle, View as DefaultView, ViewStyle } from 'react-native';
// import { TextInputProps } from "react-native-paper/lib/typescript/index";
import { HelperText as HelperTextDefault, TextInput as TextInputDefault } from 'react-native-paper';
import { TextInputProps } from 'react-native-paper/lib/typescript/components/TextInput/TextInput.d';
import HelperTextProps from 'react-native-paper/lib/typescript/components/HelperText.d';
import Colors from '../constants/Colors';
import useColorScheme from '../hooks/useColorScheme';
/**
 * Theme Color hook
 */
export function useThemeColor(
  props: { light?: string; dark?: string },
  // eslint-disable-next-line prettier/prettier
  colorName: keyof typeof Colors.light & keyof typeof Colors.dark
) {
  const theme = useColorScheme();
  const colorFromProps = props[theme];

  if (colorFromProps) {
    return colorFromProps;
  }
  return Colors[theme][colorName];
}

type ThemeProps = {
  lightColor?: string;
  darkColor?: string;
  backgroundColor?: string;
};

/**
 * Themed Base Text and View Components
 */

export type TextProps = ThemeProps & DefaultText['props'];
export type ViewProps = ThemeProps & DefaultView['props'];

export function Text(props: PropsWithChildren<TextProps>) {
  const { style, lightColor, darkColor, ...otherProps } = props;
  const color = useThemeColor({ light: lightColor, dark: darkColor }, 'text');

  return <DefaultText style={[{ color, fontFamily: 'Quicksand' }, style]} {...otherProps} />;
}
Text.defaultProps = {
  lightColor: Colors.snapCalorie.textColor,
  darkColor: Colors.snapCalorie.textColor,
};

export function View(props: ViewProps) {
  const { style, backgroundColor, ...otherProps } = props;
  return <DefaultView style={[{ backgroundColor }, style]} {...otherProps} />;
}
View.defaultProps = {
  lightColor: '',
  darkColor: '',
  backgroundColor: 'transparent',
};

/**
 * Layout Containers
 */

type RegionProps = {
  children: JSX.Element[] | JSX.Element | boolean;
  style?: ViewStyle | ViewStyle[];
};

export function HBox({ children, style }: RegionProps) {
  return <View style={[{ flexDirection: 'row' }, style]}>{children}</View>;
}
HBox.defaultProps = {
  style: {},
};

export function VBox({ children, style }: RegionProps) {
  return <View style={[{ flexDirection: 'column' }, style]}>{children}</View>;
}
VBox.defaultProps = {
  style: {},
};

type SplitBoxProps = {
  flex: number[];
  mobileView: boolean;
  reverseColumn?: boolean;
};
export function SplitBox({ children, style, flex, mobileView, reverseColumn }: RegionProps & SplitBoxProps) {
  if (!Array.isArray(children) && typeof children === 'boolean') {
    return null;
  }

  if (!Array.isArray(children) && React.isValidElement(children)) {
    children = [children];
  }

  return (
    // eslint-disable-next-line no-nested-ternary
    <View style={[{ flexDirection: mobileView ? (reverseColumn ? 'column-reverse' : 'column') : 'row' }, style]}>
      {flex.map((flexSize, ix) => {
        const flexChildren = children as JSX.Element[];

        const flexVal = mobileView ? {} : { flex: flexSize };

        if (flexChildren.length >= ix) {
          return (
            <View style={flexVal} key={ix}>
              {flexChildren[ix]}
            </View>
          );
        }

        if (!mobileView) {
          return <View style={flexVal} />;
        }

        return <View />;
      })}
    </View>
  );
}
SplitBox.defaultProps = {
  reverseColumn: false,
  style: {},
};

/**
 * Stylized Text and View Components
 */

type TextWrapProps = {
  style?: TextStyle | TextStyle[];
} & TextProps;

export function MonoText(props: TextProps) {
  return <Text {...props} style={[props.style, { fontFamily: 'space-mono' }]} />;
}

export function TextWrap({ children, style, ...otherProps }: PropsWithChildren<TextWrapProps>) {
  return (
    <View
      style={{
        maxWidth: Dimensions.get('window').width,
        alignSelf: 'center',
      }}
    >
      <Text style={[style, { flex: 1, flexWrap: 'wrap' }]} {...otherProps}>
        {children}
      </Text>
    </View>
  );
}

TextWrap.defaultProps = {
  style: {},
};

/**
 * Text Input from React-native Paper
 */
export function TextInput(props: Omit<Omit<TextInputProps, 'autoComplete'>, 'theme'>): JSX.Element {
  const { style, ...otherProps } = props;
  return (
    <TextInputDefault
      autoComplete="off"
      style={[styles.textInput, style]}
      underlineColor="#2364AA"
      activeUnderlineColor="#2364AA"
      {...otherProps}
    />
  );
}

type HelperTextProps_ = {
  children: string | Element | Element[];
  style?: TextStyle | TextStyle[];
  visible: boolean;
} & Omit<Omit<typeof HelperTextProps, 'onPressIn'>, 'onPressOut'>;

export function HelperText(props: PropsWithChildren<HelperTextProps_>): JSX.Element {
  const { style, children, visible, ...otherProps } = props;
  return (
    <HelperTextDefault
      style={[styles.invalidText, style]}
      onPressIn={() => {}}
      onPressOut={() => {}}
      type="error"
      visible={visible}
      {...otherProps}
    >
      {children}
    </HelperTextDefault>
  );
}

export const styles = StyleSheet.create({
  container: {
    overflow: 'hidden',
  },
  textInput: {
    height: 40,
    marginTop: 12,
    borderWidth: 1,
    padding: 10,
    borderBottomEndRadius: 10,
    borderTopEndRadius: 10,
    borderBottomStartRadius: 10,
    borderTopStartRadius: 10,
    overflow: 'hidden',
    textAlign: 'center',
    backgroundColor: 'white',
  },
  h1: {
    fontSize: 52,
    letterSpacing: -0.6,
    lineHeight: 50,
    padding: 10,
    color: Colors.snapCalorie.linkColor,
    fontWeight: '700',
  },
  h2: {
    fontSize: 42,
    marginTop: 10,
    color: Colors.snapCalorie.linkColor,
    fontWeight: '700',
  },
  h3: {
    fontSize: 36,
    marginTop: 10,
    color: Colors.snapCalorie.linkColor,
    fontWeight: '700',
  },
  h4: {
    fontSize: 20,
    color: Colors.snapCalorie.linkColor,
    fontWeight: '600',
  },
  focusText: {
    padding: 10,
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 28,
    color: Colors.snapCalorie.textColor,
  },
  blockParagraph: {
    padding: 10,
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 28,
    color: Colors.snapCalorie.textColor,
  },
  link: {
    fontSize: 16,
    color: Colors.snapCalorie.linkColor,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  linkText: {
    fontSize: 14,
    color: '#2e78b7',
  },
  invalidText: {
    fontWeight: '500',
    color: 'red',
    fontSize: 14,
    justifyContent: 'center',
    textAlign: 'center',
  },
  hintText: {
    fontWeight: '500',
    color: Colors.snapCalorie.textColor,
    fontSize: 14,
    justifyContent: 'center',
    textAlign: 'center',
  },
  buttonText: {
    fontSize: 20,
    fontWeight: '600',
    lineHeight: 25,
    letterSpacing: -0.29,
    color: '#F8F5F4',
  },
  buttonWrapper: {
    borderRadius: 21,
    width: 300,
    height: 50,
    marginTop: 40,
    marginBottom: 10,
    alignSelf: 'center',
  },
});
